import * as martinez from 'martinez-polygon-clipping';

import { Rr } from './Rr.js';
import { V2, XY } from './V2.js';

import { Numr } from './Numr.js';
import { Rndm } from './Rndm.js';
import { Conv } from './Conv.js';
import { Arry } from './Arry.js';
import { Ease } from './Ease.js';

export type BOX = { x: number, y: number, xx: number, yy: number, [key: string]: any; };
export type XYZR = { x: number, y: number, z: number, r: number, [key: string]: any; };
export type XYZRT = { x: number, y: number, z: number, r: number, t: number, [key: string]: any; };
export type RECT = { x: number, y: number, width: number, height: number, [key: string]: any; };
export type ELLIPSE = { x?: number, y?: number, rotation?: number, width: number, height: number, [key: string]: any; };

//2d shapes geometrie, output {x y} (XY)
export const G2 = {

    pp: Math.PI * 2,

    new: {
        offsets: function (ia: XY[], x = 0, y = 0) {
            const output = Arry.clone(ia); //um .type = 'edge' nicht zu verlieren, beispielsweise
            for (let i = 0; i < ia.length; i++) {
                output[i].x += x;
                output[i].y += y;
            }
            return output;
        },
        scales: function (ia: XY[], scale = 1) {
            const output = Arry.clone(ia); //um .type = 'edge' nicht zu verlieren, beispielsweise
            for (let i = 0; i < ia.length; i++) {
                output[i].x *= scale;
                output[i].y *= scale;
            }
            return output;
        },
        rotates: function (ia: XY[], r = 0.0) {  //done
            const output = Arry.clone(ia); //um .type = 'edge' nicht zu verlieren, beispielsweise
            const radian = G2.pp * r;
            for (let i = 0; i < ia.length; i++) {
                console.log(ia[i])
                output[i].x = ia[i].x + Math.cos(radian);
                output[i].y = ia[i].y + Math.sin(radian);
            }
            return output;
        },
        noise: function (ia: XY[], options: any = {}): XY[] {  //done
            const { amp = 0.025, res = 150, speed = 0.1, time = 0 } = options;
            const output = new Array(ia.length);
            for (let i = 0; i < ia.length; i++) {
                output[i] = { x: ia[i].x + Rndm.fast.GetNoise(ia[i].x * res, time * speed) * amp, y: ia[i].y + Rndm.fast.GetNoise(ia[i].x * res, time * speed) * amp };
            }
            return output;
        }
    },
    transform: {
        point: function (p: XY, world: XYZR): XY {
            const a = G2.pp * world.r;
            const cos = Math.cos(a);
            const sin = Math.sin(a);
            return { x: world.x + (p.x * cos - p.y * sin) * world.z, y: world.y + (p.x * sin + p.y * cos) * world.z };
        },
        points: function (ia: XY[], world: XYZR): XY[] {
            const a = G2.pp * world.r;
            const cos = Math.cos(a);
            const sin = Math.sin(a);
            const output = new Array(ia.length);
            for (let i = 0; i < ia.length; i++) {
                output[i] = { x: world.x + (ia[i].x * cos - ia[i].y * sin) * world.z, y: world.y + (ia[i].x * sin + ia[i].y * cos) * world.z };
            }
            return output;
        },
        combine: function (parent: XYZR, local: XYZR): XYZR {
            const p = G2.transform.point(local, parent);
            return { x: p.x, y: p.y, z: parent.z * local.z, r: parent.r + local.r };
        },
        inverse: function (parent: XYZR, child_world: XYZR): XYZR {
            const a = G2.pp * parent.r;
            const cos = Math.cos(a);
            const sin = Math.sin(a);
            const rel_x = child_world.x - parent.x;
            const rel_y = child_world.y - parent.y;
            return {
                x: (rel_x * cos + rel_y * sin) / parent.z,
                y: (-rel_x * sin + rel_y * cos) / parent.z,
                z: child_world.z / parent.z,
                r: child_world.r - parent.r,
            };
        },
        sampler: function (keys: XYZRT[], t: number): XYZRT { //sucht das paar keyframes um t herum (circular 0-1) und lerped dazwischen
            const n = keys.length;
            if (n === 0) return { t, x: 0, y: 0, z: 1, r: 0 };
            if (n === 1) return keys[0];
            for (let i = 0; i < n - 1; i++) {
                const a = keys[i], b = keys[i + 1];
                let span = b.t - a.t;
                if (span < 0) span += 1;
                let pos = t - a.t;
                if (pos < 0) pos += 1;
                if (pos <= span) {
                    const f = span > 0 ? pos / span : 0;
                    const dr = Rr.get.shortest_difference_float(a.r, b.r);
                    return { t, x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, z: a.z + (b.z - a.z) * f, r: a.r + dr * f };
                }
            }
            const first = keys[0], last = keys[n - 1];
            let df = Math.abs(t - first.t); df = Math.min(df, 1 - df);
            let dl = Math.abs(t - last.t); dl = Math.min(dl, 1 - dl);
            return df < dl ? first : last;
        },
        set: function (target: XYZR, from: XYZR): void {
            target.x = from.x;
            target.y = from.y;
            target.z = from.z;
            target.r = from.r;
        },
        add: function (target: XYZR, from: XYZR): void {
            target.x += from.x;
            target.y += from.y;
            target.z += from.z;
            target.r += from.r;
        },
        rotate_around: function (origin: XYZR, pivot_from: XY, pivot_to: XY, theta: number, scale: number = 1): XYZR {
            const cos_t = Math.cos(theta);
            const sin_t = Math.sin(theta);
            const dx = origin.x - pivot_from.x;
            const dy = origin.y - pivot_from.y;
            return {
                x: pivot_to.x + (cos_t * dx - sin_t * dy) * scale,
                y: pivot_to.y + (sin_t * dx + cos_t * dy) * scale,
                z: origin.z * scale,
                r: origin.r + theta / (Math.PI * 2),
            };
        }
    },
    set: {
        offsets: function (ia: XY[], x = 0, y = 0) {
            for (let i = 0; i < ia.length; i++) {
                ia[i].x += x;
                ia[i].y += y;
            }
        },
        rotates: function (ia: XY[], r = 0) {
            const radian = G2.pp * r;
            for (let i = 0; i < ia.length; i++) {
                const x = ia[i].x;
                const y = ia[i].y;
                ia[i].x = x * Math.cos(radian) - y * Math.sin(radian);
                ia[i].y = x * Math.sin(radian) + y * Math.cos(radian);
            }
        },
        tilt: function (ia: XY[], r = 0) { //not testet
            for (let i = 0; i < ia.length; i++) {
                ia[i].x = ia[i].x + (ia[i].y) * Math.sin(G2.pp * r);;
                ia[i].y = ia[i].y + (ia[i].x) * Math.cos(G2.pp * r);;
            }
        },
        tilt_x: function (ia: XY[], r = 0) { //not testet
            const tiltFactor = Math.sin(G2.pp * r);
            for (let i = 0; i < ia.length; i++) {
                ia[i].x = ia[i].x + ia[i].y * tiltFactor;
            }
        },
        tilt_y: function (ia: XY[], r = 0) { //not testet
            const tiltFactor = Math.cos(G2.pp * r);
            for (let i = 0; i < ia.length; i++) {
                ia[i].y = ia[i].y + ia[i].x * tiltFactor;
            }
        },
        noise: function (ia: XY[], r = 0) { //tbd
            const rad = G2.pp * r;
            const c = Math.cos(rad);
            const s = Math.sin(rad);
            for (let i = 0; i < ia.length; i++) {
                const { x, y } = ia[i];
                ia[i].x = x * c - y * s;
                ia[i].y = x * s + y * c;
            }
        }
    },
    get: {
        middle: function (a: XY, b: XY): XY { //center between two points
            return { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5 };
        },
        lerp: function (a: XY, b: XY, t: number): XY { //punkt zwischen a und b, t interpoliert position dazwischen
            return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        },
        radian: function (u: XY, v: XY) { //compute clockwise angle from 2 directions vectors (forming V shape)
            const dot = u.x * v.x + u.y * v.y;
            const det = u.x * v.y - u.y * v.x;
            let angle = Math.atan2(det, dot);
            if (angle < 0) angle += G2.pp; // Convert negative angles to positive angles (clockwise from u to v)
            return angle;
        },
        center_xy: function (ia: XY[]) {
            const extrem = { x: Infinity, y: Infinity, xx: -Infinity, yy: -Infinity };
            for (let i = 0; i < ia.length; i++) {
                if (ia[i].x < extrem.x) extrem.x = ia[i].x;
                if (ia[i].y < extrem.y) extrem.y = ia[i].y;
                if (ia[i].x > extrem.xx) extrem.xx = ia[i].x;
                if (ia[i].y > extrem.yy) extrem.yy = ia[i].y;
            };
            return { x: (extrem.x + extrem.xx) * 0.5, y: (extrem.y + extrem.yy) * 0.5 };
        },
        middleDirection: function (A: XY, B: XY, C: XY) { //nöd testet
            const uBA = V2.get.direction(B, A);
            const uBC = V2.get.direction(B, C);
            const angleBCtoBA = G2.get.radian(uBC, uBA);
            const half = angleBCtoBA * 0.5;
            const halfAngleVector = {
                x: uBC.x * Math.cos(half) - uBC.y * Math.sin(half),
                y: uBC.x * Math.sin(half) + uBC.y * Math.cos(half)
            };
            V2.normalize(halfAngleVector);
            return halfAngleVector;
        },
        distance_to_line: function (p: XY, a: XY, b: XY): number { //distance from line AB to point position p 
            const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
            if (l2 == 0) return V2.get.distance(p, a);
            const t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
            const tClamped = Math.max(0, Math.min(1, t));
            return V2.get.distance(p, { x: a.x + tClamped * (b.x - a.x), y: a.y + tClamped * (b.y - a.y) });
        },
        side_of_line: function (p: XY, a: XY, b: XY): number { //on which side is p of line a to b
            return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x); //positive = left, negative = right, zero = on the line.
        },
        closest_segment: function (shape: XY[], position: XY, closed_shape: boolean = true) { //returns end points of closest line of polygon shape to position

            const closest = { a: { x: 0, y: 0 }, b: { x: 0, y: 0 }, distance: Infinity, index: { a: 0, b: 0 } };

            const limit = closed_shape ? shape.length : shape.length - 1;
            for (let i = 0; i < limit; i++) {
                const next = closed_shape ? (i + 1) % shape.length : i + 1;
                const A = shape[i];
                const B = shape[next];
                const distance = G2.get.distance_to_line(position, A, B);
                if (distance < closest.distance) {
                    closest.distance = distance;
                    closest.a = A;
                    closest.b = B;
                    closest.index.a = i;
                    closest.index.b = next;
                }
            }
            return closest;
        },
        closest_point_on_line: function (p: XY, a: XY, b: XY): XY { //returns closest point to p on line AB
            const ab = { x: b.x - a.x, y: b.y - a.y };
            const ap = { x: p.x - a.x, y: p.y - a.y };
            const len = ab.x * ab.x + ab.y * ab.y;
            const dot = ap.x * ab.x + ap.y * ab.y;
            const t = Math.min(1, Math.max(0, dot / len));
            return { x: a.x + ab.x * t, y: a.y + ab.y * t, t: t };
        },
        farthest_point_in_direction: function (polygon: XY[], directionVector: XY) {
            const check = { i: 0, point: polygon[0], max: -Infinity };
            for (let i = 0; i < polygon.length; i++) {
                const point = polygon[i];
                const dot = point.x * directionVector.x + point.y * directionVector.y;
                if (dot > check.max) {
                    check.max = dot;
                    check.point = point;
                    check.i = i;
                }
            }
            return check;
        },
        ellipse_border: function ({ width = 0.1, height = 0.2, angle = 0 }) { //width and height are halfwidth and halfheight, angle is normalised 0-1
            const theta = angle * G2.pp;
            const distance = (width * height) / Math.sqrt(Math.pow(height * Math.cos(theta), 2) + Math.pow(width * Math.sin(theta), 2));
            return distance;
        },
        ellipse_offset: function (half_width = 0.1, half_height = 0.2, angle = 0): number { //offset to ground when ellipse rolling, angle is normalised 0-1
            const radian = angle * G2.pp;
            return Math.sqrt((half_width * Math.sin(radian)) ** 2 + (half_height * Math.cos(radian)) ** 2);
        },
        rectangle_border: function ({ width = 0.1, height = 0.2, angle = 0 }) { //width and height are halfwidth and halfheight, angle is normalised 0-1
            const theta = angle * G2.pp;
            const normalizedTheta = theta % (Math.PI / 2); // Normalize angle to first quadrant (0-90 degrees)
            if (normalizedTheta <= Math.atan2(height, width)) {
                return width / Math.cos(normalizedTheta);
            } else {
                return height / Math.sin(normalizedTheta);
            }
        },
        rects_gap: function (a = { x: -0.2, y: 0.3, width: 0.3, height: 0.1 }, b = { x: 0.25, y: 0.1, width: 0.2, height: 0.3 }) { //spalt zwischen zwei nicht rotierten rechtecken
            const angle = Rr.from.points_n(a, b);
            const distance = V2.get.distance(a, b);
            const a_border = G2.get.rectangle_border({ width: a.width, height: a.height, angle: angle + 0.5 });
            const b_border = G2.get.rectangle_border({ width: b.width, height: b.height, angle: angle });
            return distance - a_border - b_border;
        },
        rects_gap_vector: function (a = { x: -0.2, y: 0.3, width: 0.3, height: 0.1 }, b = { x: 0.25, y: 0.1, width: 0.2, height: 0.3 }) { //spalt zwischen zwei nicht rotierten rechtecken
            const angle = Rr.from.points_n(a, b);
            const a_border = G2.get.rectangle_border({ width: a.width, height: a.height, angle: angle + 0.5 });
            const b_border = G2.get.rectangle_border({ width: b.width, height: b.height, angle: angle });

            const a_collision = {
                x: a.x + Math.cos(angle * G2.pp) * a_border,
                y: a.y + Math.sin(angle * G2.pp) * a_border
            };

            const b_collision = {
                x: b.x - Math.cos(angle * G2.pp) * b_border,
                y: b.y - Math.sin(angle * G2.pp) * b_border
            };

            return { x: b_collision.x - a_collision.x, y: b_collision.y - a_collision.y };
        },
        polygon_surface: function (points: XY[]) { //needs to be clockwise (Shoelace formula)
            let area = 0;
            for (let i = 0; i < points.length; i++) {
                const j = (i + 1) % points.length;
                area += points[i].x * points[j].y - points[j].x * points[i].y;
            }
            return Math.abs(area / 2);
        }
    },
    project: {
        orbit: function (rotation: number, distance: number): XY { //rotation = 0-1
            const radian = rotation * G2.pp;
            return { x: distance * Math.cos(radian), y: distance * Math.sin(radian) };
        },
        circle: function (param: { angle?: number, distance?: number, position?: XY }): XY { //angle = 0-1
            const { angle = 0.25, distance = 0.1, position = { x: 0, y: 0 } } = param;
            const radian = angle * G2.pp;
            return { x: position.x + distance * Math.cos(radian), y: position.y + distance * Math.sin(radian) };
        },
        circle_radian: function (r = Math.PI, distance = 0.5, position: XY = { x: 0, y: 0 }) { //r = radian
            return { x: position.x + distance * Math.cos(r), y: position.y + distance * Math.sin(r) };
        },
        ellipse: function (e: ELLIPSE): XY {
            const radian = (e.rotation || 0) * G2.pp;
            return { x: (e.x || 0) + e.width * Math.cos(radian), y: (e.y || 0) + e.height * Math.sin(radian) };
        },
        on_perpendicular_tangent: function (c: XY, a: XY, b: XY): XY {
            const AB = V2.new.sub(b, a);
            V2.flip_y(AB); //perpendicular direction
            V2.normalize(AB);
            const BC = V2.new.sub(c, b);
            const projectionScalar = BC.x * AB.x + BC.y * AB.y; // Project BC onto P to find the projection scalar
            const closestPoint = { x: b.x + projectionScalar * AB.x, y: b.y + projectionScalar * AB.y }; // Use the projection scalar to find the closest point on the tangent
            return closestPoint;
        },
        line: function (a: XY, b: XY, t: number): XY { //returns position on line AB using t 0-1 (can also extend line)
            return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        },
        line_distance: function (a: XY, b: XY, distance: number): XY {
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const length = Math.sqrt(dx * dx + dy * dy);
            const t = distance / length;
            return { x: a.x + dx * t, y: a.y + dy * t };
        },
        line_distance_clamp: function (a: XY, b: XY, max_distance: number): XY {
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const length = Math.sqrt(dx * dx + dy * dy);
            if (length <= max_distance) return b;
            const t = max_distance / length;
            return { x: a.x + dx * t, y: a.y + dy * t };
        },
        perpendicular: function (a: XY, b: XY, distance = 0.2, direction = "right"): XY { //extends right of b
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const length = Math.sqrt(dx * dx + dy * dy);
            const nx = dx / length;
            const ny = dy / length;
            if (direction === 'right') {
                return { x: b.x + ny * distance, y: b.y + -nx * distance };
            } else { //left
                return { x: b.x - ny * distance, y: b.y + nx * distance };
            }
        },
        perpendicular_center: function (a: XY, b: XY, distance = 0.2, direction = "right") { //extends from middle between a and b
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const length = Math.sqrt(dx * dx + dy * dy);
            const nx = dx / length;
            const ny = dy / length;
            const center = V2.get.center(a, b);
            if (direction === "right") {
                return { x: center.x + ny * distance, y: center.y + -nx * distance };
            } else { //left
                return { x: center.x - ny * distance, y: center.y + nx * distance };
            }
        }
    },
    curve: {
        b_spline: function (controlPoints: XY[], resolution: number) {

            function b_spline_t(t: number) {
                const t2 = t * t;
                const t3 = t2 * t;
                const b0 = (-t3 + 3 * t2 - 3 * t + 1) / 6;
                const b1 = (3 * t3 - 6 * t2 + 4) / 6;
                const b2 = (-3 * t3 + 3 * t2 + 3 * t + 1) / 6;
                const b3 = t3 / 6;
                return [b0, b1, b2, b3];
            }

            const curvePoints = [];
            for (let i = 0; i <= resolution; i++) {
                const t = i / resolution;
                const [b0, b1, b2, b3] = b_spline_t(t);
                const x = b0 * controlPoints[0].x + b1 * controlPoints[1].x + b2 * controlPoints[2].x + b3 * controlPoints[3].x;
                const y = b0 * controlPoints[0].y + b1 * controlPoints[1].y + b2 * controlPoints[2].y + b3 * controlPoints[3].y;
                curvePoints.push({ x: x, y: y, type: "soft" });
            }
            return curvePoints;
        },
        bspline: function (points: XY[], resolution: number, degree = 3, closed = false) {

            if (points.length < degree + 1 || resolution < 2) {
                console.log("Insufficient points or resolution too low");
                return points;
            }

            function basisFunction(i: number, k: number, t: number, knots: number[]) {
                if (k === 1) return (t >= knots[i] && t < knots[i + 1]) ? 1 : 0;
                const denom1 = knots[i + k - 1] - knots[i];
                const denom2 = knots[i + k] - knots[i + 1];
                let term1 = 0;
                let term2 = 0;
                if (denom1 !== 0) term1 = ((t - knots[i]) / denom1) * basisFunction(i, k - 1, t, knots);
                if (denom2 !== 0) term2 = ((knots[i + k] - t) / denom2) * basisFunction(i + 1, k - 1, t, knots);
                return term1 + term2;
            }

            // Generate a uniform knot vector
            function generateKnotVector(numPoints: number, degree: number, closed: boolean) {
                const knots = [];
                const totalKnots = numPoints + degree + 1;

                if (closed) {
                    // For closed curves, create a cyclic knot vector
                    for (let i = 0; i < totalKnots; i++) {
                        knots.push(i);
                    }
                } else {
                    // For open curves, use the standard knot vector
                    for (let i = 0; i < totalKnots; i++) {
                        if (i < degree + 1) {
                            knots.push(0);
                        } else if (i >= numPoints) {
                            knots.push(numPoints - degree);
                        } else {
                            knots.push(i - degree);
                        }
                    }
                }
                return knots;
            }

            // If the curve is closed, wrap the control points
            if (closed) points = points.concat(points.slice(0, degree));

            const numPoints = points.length;
            const knots = generateKnotVector(numPoints, degree, closed);
            const newPoints = [];

            // Determine the valid range for t
            const tMin = knots[degree];
            const tMax = knots[numPoints]; // knots[numPoints] is the end of the valid range
            const tStep = (tMax - tMin) / (resolution * (tMax - tMin));

            // Iterate over the curve and sample points
            for (let t = tMin; t <= tMax; t += tStep) {
                let x = 0;
                let y = 0;
                for (let i = 0; i < numPoints; i++) {
                    const basis = basisFunction(i, degree + 1, t, knots);
                    if (isNaN(basis)) {
                        console.warn(`NaN encountered in basis function for i=${i}, t=${t}`);
                        continue;
                    }
                    x += points[i].x * basis;
                    y += points[i].y * basis;
                }
                newPoints.push({ x, y });
            }
            return newPoints;
        },
        // Catmull-Rom interpolation of a single scalar component (fixed 0.5 tension).
        catmull: function (p0: number, p1: number, p2: number, p3: number, t: number) {
            return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);
        },
        // Catmull-Rom interpolation of an XY point, with adjustable tension. Single segment p1->p2.
        catmullRom: function (p0: XY, p1: XY, p2: XY, p3: XY, t: number, tension = 0.5) {

            const t2 = t * t;
            const t3 = t2 * t;

            // Catmull-Rom basis matrix
            const m0 = (-tension * t3 + 2 * tension * t2 - tension * t);
            const m1 = ((2 - tension) * t3 + (tension - 3) * t2 + 1);
            const m2 = ((tension - 2) * t3 + (3 - 2 * tension) * t2 + tension * t);
            const m3 = (tension * t3 - tension * t2);

            // Calculate the interpolated point
            const x = m0 * p0.x + m1 * p1.x + m2 * p2.x + m3 * p3.x;
            const y = m0 * p0.y + m1 * p1.y + m2 * p2.y + m3 * p3.y;

            return { x, y };
        },
        // Samples a Catmull-Rom curve through all points (tension adjustable). Endpoints are
        // extrapolated via a virtual point. See also interpolate_curve, which clamps endpoints instead.
        catmullRomSampler: function (points: XY[], resolution: number, tension = 0.5) {

            const newPoints = [];

            const extendedPoints = [
                { x: points[0].x - (points[1].x - points[0].x), y: points[0].y - (points[1].y - points[0].y) }, // Virtual start point
                ...points,
                { x: points[points.length - 1].x + (points[points.length - 1].x - points[points.length - 2].x), y: points[points.length - 1].y + (points[points.length - 1].y - points[points.length - 2].y) } // Virtual end point
            ];

            for (let i = 1; i < extendedPoints.length - 2; i++) {
                const p0 = extendedPoints[i - 1];
                const p1 = extendedPoints[i];
                const p2 = extendedPoints[i + 1];
                const p3 = extendedPoints[i + 2];

                // Sample the current segment
                for (let j = 0; j <= resolution; j++) {
                    const t = j / resolution;
                    newPoints.push(this.catmullRom(p0, p1, p2, p3, t, tension));
                }
            }
            return newPoints;
        },
        // Samples a Catmull-Rom curve through all points at fixed 0.5 tension, clamping endpoints
        // (no virtual point). Same family as catmullRomSampler but endpoint behaviour differs.
        interpolate_curve: function (points: XY[], resolution: number) {
            const result = [];
            for (let i = 0; i < points.length - 1; i++) {
                const p0 = points[Math.max(i - 1, 0)];
                const p1 = points[i];
                const p2 = points[i + 1];
                const p3 = points[Math.min(i + 2, points.length - 1)];
                for (let t = 0; t <= 1; t += 1 / resolution) {
                    const x = this.catmull(p0.x, p1.x, p2.x, p3.x, t);
                    const y = this.catmull(p0.y, p1.y, p2.y, p3.y, t);
                    result.push({ x, y });
                }
            }
            return result;
        },
        interpolate_curve_position: function (points: XY[], t: number) {

            // Determine the segment index based on t
            const numSegments = points.length - 1;
            const segmentT = t * numSegments; // Scaled t
            const segmentIndex = Math.min(Math.floor(segmentT), numSegments - 1);
            const localT = segmentT - segmentIndex; // Local t for the segment

            // Define segment control points
            const p0 = points[Math.max(segmentIndex - 1, 0)];
            const p1 = points[segmentIndex];
            const p2 = points[segmentIndex + 1];
            const p3 = points[Math.min(segmentIndex + 2, points.length - 1)];

            // Interpolate position
            const x = this.catmull(p0.x, p1.x, p2.x, p3.x, localT);
            const y = this.catmull(p0.y, p1.y, p2.y, p3.y, localT);

            return { x, y };
        },
        cubic_bezier: function (p0: XY, p1: XY, p2: XY, p3: XY, t: number) {
            const cx = 3 * (p1.x - p0.x);
            const bx = 3 * (p2.x - p1.x) - cx;
            const ax = p3.x - p0.x - cx - bx;

            const cy = 3 * (p1.y - p0.y);
            const by = 3 * (p2.y - p1.y) - cy;
            const ay = p3.y - p0.y - cy - by;

            const x = ax * Math.pow(t, 3) + bx * Math.pow(t, 2) + cx * t + p0.x;
            const y = ay * Math.pow(t, 3) + by * Math.pow(t, 2) + cy * t + p0.y;

            return { x, y };
        },
        bezier: function (points: XY[], resolution: number) { //only works with 4 points
            const newPoints = [];
            for (let i = 0; i <= resolution; i++) {
                const t = i / resolution;
                newPoints.push(this.cubic_bezier(points[0], points[1], points[2], points[3], t));
            }
            return newPoints;
        },
        sampleBezierCurveLong: function (points: XY[], resolution: number) {
            const newPoints = [];
            for (let i = 0; i < points.length - 1; i += 3) {
                const p0 = points[i];
                const p1 = points[i + 1] || p0; // Fallback to p0 if not enough points
                const p2 = points[i + 2] || p1; // Fallback to p1 if not enough points
                const p3 = points[i + 3] || p2; // Fallback to p2 if not enough points

                for (let j = 0; j <= resolution; j++) {
                    const t = j / resolution;
                    newPoints.push(this.cubic_bezier(p0, p1, p2, p3, t));
                }
            }
            return newPoints;
        },
        sample_catmul_fixed_n: function (points: XY[], out: XY[], offset_x = 0, normals?: XY[]) { // Zero-alloc Catmull-Rom sampler, kann auch normals berechnen
            const n = points.length;
            const segs = n - 1;
            const last = out.length - 1;
            for (let i = 0; i <= last; i++) {
                const tg = (i / last) * segs;
                let seg = tg | 0; if (seg > segs - 1) seg = segs - 1;
                const lt = tg - seg;
                const p0 = points[seg - 1 < 0 ? 0 : seg - 1];
                const p1 = points[seg];
                const p2 = points[seg + 1];
                const p3 = points[seg + 2 > n - 1 ? n - 1 : seg + 2];
                const t2 = lt * lt, t3 = t2 * lt;
                const w0 = 0.5 * (-t3 + 2 * t2 - lt);
                const w1 = 0.5 * (3 * t3 - 5 * t2 + 2);
                const w2 = 0.5 * (-3 * t3 + 4 * t2 + lt);
                const w3 = 0.5 * (t3 - t2);
                out[i].x = p0.x * w0 + p1.x * w1 + p2.x * w2 + p3.x * w3 + offset_x;
                out[i].y = p0.y * w0 + p1.y * w1 + p2.y * w2 + p3.y * w3;
            }
            if (normals) {
                for (let i = 0; i <= last; i++) {
                    const a = out[i - 1 < 0 ? 0 : i - 1];
                    const b = out[i + 1 > last ? last : i + 1];
                    let tx = b.x - a.x;
                    let ty = b.y - a.y;
                    const len = Math.hypot(tx, ty) || 1;
                    tx /= len; ty /= len;
                    normals[i].x = -ty;
                    normals[i].y = tx;
                }
            }
        },
    },
    line: {
        extend: function (a: XY, b: XY, distance_a = 0.1, distance_b = distance_a) {  //macht line auf beiden seiten kürzer, länger

            // Calculate the direction vector from a to b
            const dx = b.x - a.x;
            const dy = b.y - a.y;

            const length = Math.sqrt(dx * dx + dy * dy);
            const ux = dx / length;
            const uy = dy / length;

            const new_a = { x: a.x - ux * distance_a, y: a.y - uy * distance_a };
            const new_b = { x: b.x + ux * distance_b, y: b.y + uy * distance_b };

            return { a: new_a, b: new_b };
        },
        dashed: function ({ a, b, dashes = 5, gaps = 0.01, full_length = false }: { a: XY; b: XY; dashes: number; gaps: number; full_length: boolean; }) {

            const result = [];
            const distance = V2.get.distance(a, b);
            const dashLength = full_length ? (distance - gaps * (dashes - 1)) / dashes : (distance / dashes - gaps);
            const startOffset = full_length ? 0 : gaps / 2;

            for (let i = 0; i < dashes; i++) {

                const startFraction = startOffset + i * (dashLength + gaps);
                const endFraction = startFraction + dashLength;

                const startX = a.x + (b.x - a.x) * (startFraction / distance);
                const startY = a.y + (b.y - a.y) * (startFraction / distance);
                const endX = a.x + (b.x - a.x) * (endFraction / distance);
                const endY = a.y + (b.y - a.y) * (endFraction / distance);

                result.push([{ x: startX, y: startY }, { x: endX, y: endY }]);
            }
            return result;
        },
        dashed_fixed: function ({ a, b, long = 0.1, n = 10 }: { a: XY; b: XY; long: number; n: number; }) { //always keeps n

            const result = [];
            const totalDistance = V2.get.distance(a, b);
            const totalDashSpace = long * n;
            const gapLength = n > 1 ? (totalDistance - totalDashSpace) / (n - 1) : 0;

            let currentPosition = 0;

            for (let i = 0; i < n; i++) {

                // Dash start and end - cap at total distance
                const dashStart = Math.min(currentPosition, totalDistance);
                const dashEnd = Math.min(dashStart + long, totalDistance);

                // Calculate coordinates
                const startX = a.x + (b.x - a.x) * (dashStart / totalDistance);
                const startY = a.y + (b.y - a.y) * (dashStart / totalDistance);
                const endX = a.x + (b.x - a.x) * (dashEnd / totalDistance);
                const endY = a.y + (b.y - a.y) * (dashEnd / totalDistance);

                result.push([{ x: startX, y: startY }, { x: endX, y: endY }]);

                currentPosition = dashEnd + gapLength; // Move to next position (dash + gap) - gap can be negative
            }

            return result;
        },
        interpolate: function (a: XY, b: XY, segements = 6) { //interpolates line
            const points = [];
            for (let i = 0; i <= segements; i++) {
                const t = i / segements;
                const x = a.x + t * (b.x - a.x);
                const y = a.y + t * (b.y - a.y);
                points.push({ x, y });
            }
            return points;
        },
        interpolates: function (points: XY[], resolution: number) {

            function helper(point1: XY, point2: XY, fraction: number) {
                return {
                    x: point1.x + (point2.x - point1.x) * fraction,
                    y: point1.y + (point2.y - point1.y) * fraction
                };
            }

            if (points.length < 2 || resolution < 2) {
                return points;
            }

            const newPoints = [];
            const originalSegments = points.length - 1;
            const newSegments = resolution - 1;
            const segmentLength = originalSegments / newSegments;

            for (let i = 0; i <= newSegments; i++) {
                const exactPosition = i * segmentLength;
                const index = Math.floor(exactPosition);
                const fraction = exactPosition - index;

                if (index < originalSegments) {
                    newPoints.push(helper(points[index], points[index + 1], fraction));
                } else {
                    newPoints.push(points[points.length - 1]);
                }
            }
            return newPoints;
        },
    },
    collision: {
        rect_center: function (rect: RECT, xy: XY, padding: number = 0): boolean { //xy is center, width and height are halfs
            return xy.x > rect.x - rect.width - padding && xy.y > rect.y - rect.height - padding && xy.x < rect.x + rect.width + padding && xy.y < rect.y + rect.height + padding;
        },
        rect: function (rect: RECT, xy: XY, padding: number = 0): boolean { //xy are top left
            return xy.x > rect.x - padding && xy.y > rect.y - padding && xy.x < rect.x + rect.width + padding && xy.y < rect.y + rect.height + padding;
        },
        rects: function (rects: RECT[], xy: XY, padding: number = 0): boolean {
            for (const rect of rects) {
                if (this.rect(rect, xy, padding)) return true;
            }
            return false;
        },
        box: function (box: BOX, point: XY, padding: number = 0): boolean {
            return point.x > box.x - padding && point.x < box.xx + padding && point.y > box.y - padding && point.y < box.yy + padding;
        },
        boxes: function (boxes: BOX[], xy: XY): boolean {
            for (const box of boxes) {
                if (this.box(box, xy)) return true;
            }
            return false;
        },
        polygon: function (shape: XY[], position: XY) {
            let count = 0
            for (let i = 0; i < shape.length; i++) {
                const next = (i + 1) % shape.length;
                const x1 = shape[i].x;
                const y1 = shape[i].y;
                const x2 = shape[next].x;
                const y2 = shape[next].y;
                const minY = Math.min(y1, y2);
                const maxY = Math.max(y1, y2);
                if (position.y <= minY || position.y > maxY) continue;
                const edgeX = (position.y - y1) * (x2 - x1) / (y2 - y1) + x1;
                if (edgeX > position.x) count++;
            }
            return count % 2 == 1;
        },
        ellipse: function (click: XY, center: XY, width: number, height: number, padding = 0, rotation = 0) {
            const angle = rotation * G2.pp;
            const translatedX = click.x - center.x;
            const translatedY = click.y - center.y;
            const cosAngle = Math.cos(-angle);
            const sinAngle = Math.sin(-angle);
            const rotatedX = translatedX * cosAngle - translatedY * sinAngle;
            const rotatedY = translatedX * sinAngle + translatedY * cosAngle;
            const normalizedX = rotatedX / (width + padding);
            const normalizedY = rotatedY / (height + padding);
            return (normalizedX * normalizedX + normalizedY * normalizedY) <= 1;
        },
        line_ellipse: function (a: XY, b: XY, center: XY, height: number, width: number) {

            // Returns true if infinite line through points a and b 
            // intersects ellipse at another point besides possibly a

            const x0 = a.x - center.x;
            const y0 = a.y - center.y;
            const dx = b.x - a.x;
            const dy = b.y - a.y;

            // Skip if line has no direction
            if (Math.abs(dx) < 1e-10 && Math.abs(dy) < 1e-10) return false;

            const A = (dx * dx) / (width * width) + (dy * dy) / (height * height);
            const B = 2 * ((x0 * dx) / (width * width) + (y0 * dy) / (height * height));
            const C = (x0 * x0) / (width * width) + (y0 * y0) / (height * height) - 1;

            const discriminant = B * B - 4 * A * C;
            if (discriminant <= 0) return false;

            const sqrtDisc = Math.sqrt(discriminant);
            const t1 = (-B + sqrtDisc) / (2 * A);
            const t2 = (-B - sqrtDisc) / (2 * A);

            return Math.abs(t1) > 1e-10 || Math.abs(t2) > 1e-10;

        },
        point_line: function (point: XY, shape: XY[], padding = 0.1) { //padding means min dist to line

            const output: {
                distance: number;
                point: XY | null;
                segment: number;
                side: number;
            } = {
                distance: Infinity,
                point: null,
                segment: -1,
                side: 0
            };

            for (let i = 0; i < shape.length; i++) { // Check each line segment in the shape

                const start = shape[i];
                const end = shape[(i + 1) % shape.length]; // Wrap around to connect last to first

                const closest = G2.get.closest_point_on_line(point, start, end);
                const dist = V2.get.distance(point, closest);

                if (dist < padding && dist < output.distance) {
                    output.distance = dist;
                    output.point = closest;
                    output.segment = i;
                    output.side = G2.get.side_of_line(point, start, end);
                }
            }

            return output;
        },
        line_line: function (lineA: XY[], lineB: XY[], padding = 0.1) {

            const output: {
                distance: number;
                point: XY | null;
                segment: number;
                side: number;
                point_index: number;
            } = {
                distance: Infinity,
                point: null,
                segment: -1,
                side: 0,
                point_index: -1
            };

            for (let p = 0; p < lineA.length; p++) {
                const point = lineA[p];

                for (let i = 0; i < lineB.length; i++) {
                    const start = lineB[i];
                    const end = lineB[(i + 1) % lineB.length];

                    const closest = G2.get.closest_point_on_line(point, start, end);
                    const dist = V2.get.distance(point, closest);

                    if (dist < padding && dist < output.distance) {
                        output.distance = dist;
                        output.point = closest;
                        output.segment = i;
                        output.side = G2.get.side_of_line(point, start, end);
                        output.point_index = p;
                    }
                }
            }

            return output;
        },
        line_line_all: function (lineA: XY[], lineB: XY[], padding = 0.1) {

            const hits: {
                point_index: number;
                point: XY;
                closest: XY;
                distance: number;
                segment: number;
                side: number;
                normal: XY;
            }[] = [];

            for (let p = 0; p < lineA.length; p++) {
                const point = lineA[p];
                let best_dist = Infinity;
                let best_closest: XY | null = null;
                let best_segment = -1;
                let best_side = 0;

                for (let i = 0; i < lineB.length; i++) {
                    const start = lineB[i];
                    const end = lineB[(i + 1) % lineB.length];
                    const closest = G2.get.closest_point_on_line(point, start, end);
                    const dist = V2.get.distance(point, closest);
                    if (dist < padding && dist < best_dist) {
                        best_dist = dist;
                        best_closest = closest;
                        best_segment = i;
                        best_side = G2.get.side_of_line(point, start, end);
                    }
                }

                if (best_closest) {
                    const nx = best_dist > 0 ? (point.x - best_closest.x) / best_dist : 0;
                    const ny = best_dist > 0 ? (point.y - best_closest.y) / best_dist : 0;
                    hits.push({
                        point_index: p,
                        point,
                        closest: best_closest,
                        distance: best_dist,
                        segment: best_segment,
                        side: best_side,
                        normal: { x: nx, y: ny }
                    });
                }
            }

            return hits;
        }
    },
    operation: {
        subtract: function (shape1: XY[], shape2: XY[]) { //subtracts shape2 from shape1
            const s1 = [Conv.xy_array(shape1)];
            const s2 = [Conv.xy_array(shape2)];
            const subract = martinez.diff(s1, s2);
            const flatten = Conv.array_xy(subract[0][0]);
            return flatten;
        }
    },
    shape: {
        rect_oriented: function (a: XY, b: XY, width: number): XY[] { //4 corners of rect spanning from a to b, width is half-extent perpendicular to a->b
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const len = Math.sqrt(dx * dx + dy * dy) || 1;
            const px = -dy / len * width;
            const py = dx / len * width;
            return [
                { x: a.x - px, y: a.y - py },
                { x: a.x + px, y: a.y + py },
                { x: b.x + px, y: b.y + py },
                { x: b.x - px, y: b.y - py },
            ];
        },
        rubberband: function (a: ELLIPSE, b: ELLIPSE, res = 48) {

            const a_ellipse = G2.shape.ellipse({ x: a.x, y: a.y, height: a.height, width: a.width, rotation: a.rotation || 0, res });
            const b_ellipse = G2.shape.ellipse({ x: b.x, y: b.y, height: b.height, width: b.width, rotation: b.rotation || 0.5, res });

            const dir = V2.get.direction(b as XY, a as XY);
            V2.flip_x(dir); //90 grad uhrzeigersinn

            function extremes(polygon: XY[], d: XY) {
                let maxI = 0, minI = 0, maxD = -Infinity, minD = Infinity;
                for (let i = 0; i < polygon.length; i++) {
                    const dot = polygon[i].x * d.x + polygon[i].y * d.y;
                    if (dot > maxD) { maxD = dot; maxI = i; }
                    if (dot < minD) { minD = dot; minI = i; }
                }
                return { maxI, minI };
            }

            const ea = extremes(a_ellipse, dir);
            const eb = extremes(b_ellipse, dir);

            const a_curve = ea.minI <= ea.maxI
                ? a_ellipse.slice(ea.minI, ea.maxI)
                : [...a_ellipse.slice(ea.minI), ...a_ellipse.slice(0, ea.maxI)];
            const b_curve = eb.maxI <= eb.minI
                ? b_ellipse.slice(eb.maxI, eb.minI)
                : [...b_ellipse.slice(eb.maxI), ...b_ellipse.slice(0, eb.minI)];
            return [...a_curve, ...b_curve];
        },
        rubberband_points: function (a: ELLIPSE, b: ELLIPSE, iterations = 5) {

            const ra = (a.rotation || 0) * G2.pp;
            const rb = (b.rotation || 0) * G2.pp;
            const cos_ra = Math.cos(ra), sin_ra = Math.sin(ra);
            const cos_rb = Math.cos(rb), sin_rb = Math.sin(rb);

            function support(cx: number, cy: number, w: number, h: number, cos_r: number, sin_r: number, nx: number, ny: number) {
                const lnx = nx * cos_r + ny * sin_r;
                const lny = -nx * sin_r + ny * cos_r;
                const theta = Math.atan2(h * lny, w * lnx);
                const lx = w * Math.cos(theta);
                const ly = h * Math.sin(theta);
                return { x: cx + lx * cos_r - ly * sin_r, y: cy + lx * sin_r + ly * cos_r };
            }

            const ax = a.x || 0, ay = a.y || 0, aw = a.width, ah = a.height;
            const bx = b.x || 0, by = b.y || 0, bw = b.width, bh = b.height;

            const dir = V2.get.direction(b as XY, a as XY);
            const perp = Math.atan2(dir.x, -dir.y);

            function f(phi: number) {
                const nx = Math.cos(phi);
                const ny = Math.sin(phi);
                const pa = support(ax, ay, aw, ah, cos_ra, sin_ra, nx, ny);
                const pb = support(bx, by, bw, bh, cos_rb, sin_rb, nx, ny);
                return (pa.x - pb.x) * nx + (pa.y - pb.y) * ny;
            }

            function solve(center: number) { //bisection: narrow search by half each step
                let lo = center - Math.PI * 0.49;
                let hi = center + Math.PI * 0.49;
                let flo = f(lo);
                for (let i = 0; i < iterations; i++) {
                    const mid = (lo + hi) * 0.5;
                    const fmid = f(mid);
                    if (flo * fmid <= 0) { hi = mid; }
                    else { lo = mid; flo = fmid; }
                }
                return (lo + hi) * 0.5;
            }

            const phi1 = solve(perp);
            const phi2 = solve(perp + Math.PI);

            const nx1 = Math.cos(phi1), ny1 = Math.sin(phi1);
            const nx2 = Math.cos(phi2), ny2 = Math.sin(phi2);

            const p1 = support(ax, ay, aw, ah, cos_ra, sin_ra, nx1, ny1);
            const p2 = support(bx, by, bw, bh, cos_rb, sin_rb, nx1, ny1);
            const p3 = support(ax, ay, aw, ah, cos_ra, sin_ra, nx2, ny2);
            const p4 = support(bx, by, bw, bh, cos_rb, sin_rb, nx2, ny2);

            return [[p3, p4], [p1, p2]];
        },
        pill: function (a: ELLIPSE, b: ELLIPSE, res = 20) {
            const angle = Rr.from.points_n(a as XY, b as XY);
            const a_ellipse = G2.shape.ellipse({ x: a.x, y: a.y, height: a.height, width: a.width, rotation: angle, res, start: 0.25, end: 0.75 });
            const b_ellipse = G2.shape.ellipse({ x: b.x, y: b.y, height: b.height, width: b.width, rotation: angle - 0.5, res, start: 0.25, end: 0.75 });
            const output = [...a_ellipse, ...b_ellipse];
            return output;
        },
        pill_tilted: function (a: ELLIPSE, b: ELLIPSE, res = 20, tilt = 0) {
            const a_ellipse = G2.shape.ellipse({ x: a.x, y: a.y, height: a.height, width: a.width, res, start: 0.5 + tilt, end: 1.0 + tilt });
            const b_ellipse = G2.shape.ellipse({ x: b.x, y: b.y, height: b.height, width: b.width, res, start: 0.5 + tilt, end: 1.5 + tilt, rotation: 0.5 });
            const output = [...a_ellipse, ...b_ellipse];
            return output;
        },
        contour_line: function (points: XY[], long = 0.5, x = 0, y = 0, rotation = 0, tilt = 0) {
            const output = [];
            for (const p of points) {
                output.push({ x: p.w, y: (p.t * 2 - 1) * long });
            }
            //if (tilt) G2.set.tilt(output, 0.2, 0.1, tilt);
            if (rotation) G2.set.rotates(output, rotation);
            if (tilt) G2.set.tilt_y(output, tilt);
            G2.set.offsets(output, x, y);
            return output;
        },
        polygon_bspline: function (points: XY[], res = 12, closed = true) { //points type = "soft" / "hard"

            const output = [];

            if (closed) {

                for (let i = 0; i < points.length; i++) {
                    const p0 = points[Numr.roll(i - 1, points.length)];
                    const p1 = points[Numr.roll(i - 0, points.length)];
                    const p2 = points[Numr.roll(i + 1, points.length)];
                    const p3 = points[Numr.roll(i + 2, points.length)];

                    const curvePoints = G2.curve.b_spline([p0, p1, p2, p3], res);
                    output.push(...curvePoints);
                }

            } else {

                // First segment (special handling)
                const p0_first = G2.project.line(points[1], points[0], 2);
                const p1_first = points[0];
                const p2_first = points[1];
                const p3_first = points[2];
                const firstCurve = G2.curve.b_spline([p0_first, p1_first, p2_first, p3_first], res);
                output.push(...firstCurve);

                // Middle segments (full 4-point segments)
                for (let i = 1; i < points.length - 2; i++) {
                    const p0 = points[i - 1];
                    const p1 = points[i];
                    const p2 = points[i + 1];
                    const p3 = points[i + 2];
                    const curvePoints = G2.curve.b_spline([p0, p1, p2, p3], res);
                    output.push(...curvePoints);
                }

                // Last segment (special handling)
                const p0_last = points[points.length - 3];
                const p1_last = points[points.length - 2];
                const p2_last = points[points.length - 1];
                const p3_last = G2.project.line(points[points.length - 2], points[points.length - 1], 2);
                const lastCurve = G2.curve.b_spline([p0_last, p1_last, p2_last, p3_last], res);
                output.push(...lastCurve);
            }

            return output;
        },
        polygon_mixed_bspline: function (param: { points: any[], res: number, closed: boolean }) { //points type = "soft" / "hard" (nur hard muss definiert werden, default ist soft)

            const { points, res = 12, closed = true } = param;

            const segments = closed ? points.length : points.length - 1;
            const output = [];

            for (let i = 0; i < segments; i++) {
                // For closed polygons, wrap around; for open, handle endpoints specially
                let p0, p1, p2, p3;

                if (closed) {
                    // Closed polygon: wrap around
                    p0 = points[Numr.roll(i - 1, points.length)];
                    p1 = points[Numr.roll(i - 0, points.length)];
                    p2 = points[Numr.roll(i + 1, points.length)];
                    p3 = points[Numr.roll(i + 2, points.length)];
                } else {
                    // Open polygon: handle endpoints
                    // At start: duplicate first point
                    // At end: duplicate last point
                    p0 = (i > 0) ? points[i - 1] : points[i];
                    p1 = points[i];
                    p2 = points[i + 1];
                    p3 = (i < points.length - 2) ? points[i + 2] : points[i + 1];
                }

                const curvePoints = G2.curve.b_spline([p0, p1, p2, p3], res);

                if (p1.type === "hard") {
                    if (p2.type === "hard") {
                        // Hard to hard: just add the straight line
                        // For last segment of open polygon, ensure we reach the endpoint
                        if (!closed && i === segments - 1) {
                            output.push(p2); // Add the actual endpoint
                        } else {
                            // For other segments, interpolate the line
                            const straight = G2.line.interpolate(p1, p2, res);
                            output.push(...straight);
                        }
                    } else {
                        // Hard to soft: interpolate from straight to curve
                        const straight = G2.line.interpolate(p1, p2, res);
                        for (let u = 0; u < res; u++) {
                            output.push(G2.get.lerp(straight[u], curvePoints[u], u / (res - 1)));
                        }
                    }
                } else {
                    if (p2.type === "hard") {
                        // Soft to hard: interpolate from curve to straight
                        const straight = G2.line.interpolate(p1, p2, res);
                        for (let u = 0; u < res; u++) {
                            output.push(G2.get.lerp(curvePoints[u], straight[u], u / (res - 1)));
                        }
                        // Ensure we reach the endpoint for last segment
                        if (!closed && i === segments - 1) output.push(p2);
                    } else {
                        // Soft to soft: normal B-spline
                        output.push(...curvePoints);
                    }
                }
            }

            // If closed, add the first point at the end to close the loop
            if (closed && output.length > 0) {
                output.push(output[0]);
            }

            return output;
        },
        polygon_mixed_catmull: function (points: XY[], res = 12) { //points type = "soft" / "hard"

            const n = points.length;
            const out = [];

            for (let i = 0; i < n; i++) {

                const p0 = points[Numr.roll(i - 1, points.length)];
                const p1 = points[Numr.roll(i - 0, points.length)];
                const p2 = points[Numr.roll(i + 1, points.length)];
                const p3 = points[Numr.roll(i + 2, points.length)];

                if (p1.type === "soft") {
                    if (p2.type === "soft") {
                        // pure Catmull-Rom segment
                        for (let j = 0; j <= res; j++) {
                            const t = j / res;
                            out.push(G2.curve.catmullRom(p0, p1, p2, p3, t));
                        }
                    } else {
                        // soft -> edge: blend Catmull-Rom to straight line
                        const straight = G2.line.interpolate(p1, p2, res);
                        for (let j = 0; j <= res; j++) {
                            const t = j / res;
                            const curvePt = G2.curve.catmullRom(p0, p1, p2, p3, t);
                            out.push(G2.get.lerp(curvePt, straight[j], t));
                        }
                    }
                } else {
                    if (p2.type === "hard") {
                        // hard -> hard
                        out.push(p2);
                    } else {
                        // hard -> soft: blend straight -> Catmull-Rom
                        const straight = G2.line.interpolate(p1, p2, res);
                        for (let j = 0; j <= res; j++) {
                            const t = j / res;
                            const curvePt = G2.curve.catmullRom(p0, p1, p2, p3, t);
                            out.push(G2.get.lerp(straight[j], curvePt, t));
                        }
                    }
                }
            }

            return out;
        },

        polygon_catmull: function (points: XY[], res = 12) { //all points soft

            const n = points.length;
            const out = [];

            for (let i = 0; i < n; i++) {

                const p0 = points[Numr.roll(i - 1, points.length)];
                const p1 = points[Numr.roll(i - 0, points.length)];
                const p2 = points[Numr.roll(i + 1, points.length)];
                const p3 = points[Numr.roll(i + 2, points.length)];

                // pure Catmull-Rom segment
                for (let j = 0; j <= res; j++) {
                    const t = j / res;
                    out.push(G2.curve.catmullRom(p0, p1, p2, p3, t));
                }

            }

            return out;
        },
        spiral: function ({ rig, curve = 8, width = 0.6, perspective = 0.2, turns = 5, bottom = 0.5, top = 0.1, resolution = 24, log = false, turn_shift = 1.5, tilt = true, easy = "cubicOut" }: { //resolution = points per turn
            rig: XY[];
            curve?: number;
            width?: number;
            perspective?: number;
            turns?: number;
            bottom?: number;
            top?: number;
            resolution?: number;
            log?: boolean;
            turn_shift?: number;
            tilt?: boolean;
            easy?: "linear" | "cubicIn" | "cubicOut" | "cubicInOut" | "quadIn" | "quadOut" | "quadInOut" | "sineIn" | "sineOut" | "sineInOut";
        }): XY[] {

            function steps(arrayLength: number, t: number) { // in items: 8, d: 0.4 -> out: a: 3, b: 4, d: 0.2
                const precisePosition = (arrayLength - 1) * t;
                const indexA = Math.floor(precisePosition);
                const indexB = Math.min(indexA + 1, arrayLength - 1);
                const interpolationValue = precisePosition - indexA;
                return { a: indexA, b: indexB, d: interpolationValue };
            }

            function oval({ position = { x: 0, y: 0 }, width = 0.2, height = width, angle = 0, rotation = 0 }: { position?: XY; width?: number; height?: number; angle?: number; rotation?: number }): XY {
                const x = height * Math.cos(angle) * Math.cos(rotation) - width * Math.sin(angle) * Math.sin(rotation) + position.x;
                const y = height * Math.cos(angle) * Math.sin(rotation) + width * Math.sin(angle) * Math.cos(rotation) + position.y;
                return { x, y };
            }

            if (curve) rig = G2.curve.bezier(rig, curve); //interpolate rig

            const output = [];
            const total = turns * resolution;
            const turnStep = G2.pp / resolution;

            for (let y = 0; y <= total; y++) {

                const t = 1 / total * y;
                const step = steps(rig.length, t);
                const position = G2.project.line(rig[step.a], rig[step.b], step.d);
                const angle = (step.a == step.b) ? Rr.from.points(rig[step.a - 1], rig[step.b]) : Rr.from.points(rig[step.a], rig[step.b]);

                if (log) console.log(Math.trunc(t * 100) / 100, rig.length, step);
                if (log) console.log(angle);

                const scale = Numr.lerp(bottom, top, Ease[easy](t));
                const point = (tilt) ? oval({ position, rotation: angle, angle: turnStep * y + turn_shift, width: width * scale, height: perspective * scale }) : oval({ position, rotation: 0, angle: turnStep * y + turn_shift, width: width * scale, height: perspective * scale });
                output.push(point);
            }
            return output;
        },
        circle: function ({ x = 0, y = 0, size = 0.5, res = 12, rotation = 0, start = 0, end = 1 }): XY[] {
            const output = [];
            const slice = 1 / res;
            for (let i = 0; i < res; i++) {
                const angle = slice * i;
                if (angle >= start && angle <= end) {
                    output.push(G2.project.circle({ angle: angle + rotation, distance: size, position: { x, y } }));
                }
            }
            return output;
        },
        circle_from_lines: function ({ x = 0, y = 0, size = 0.5, res = 12, start = 0, end = 1, rotation = 0, long = 0.02 }): any[] { //macht viele linien die alle in die mitte zeigen
            const output = [];
            const slice = 1 / res;
            for (let i = 0; i < res; i++) {
                const angle = slice * i;
                if (angle >= start && angle <= end) {
                    const inner = G2.project.circle({ angle: angle + rotation, distance: size - long, position: { x, y } });
                    const outer = G2.project.circle({ angle: angle + rotation, distance: size + long, position: { x, y } });
                    output.push([inner, outer]);
                }
            }
            return output;
        },
        triangle: function ({ x = 0, y = 0, size = 0.5, corners = 3, angle = 0 }): XY[] { //angle 0-1
            const output = [];
            const step = 1 / corners;
            for (let i = 0; i < corners; i++) {
                const r = i * step + angle;
                output.push(G2.project.circle({ angle: r, distance: size, position: { x, y } }));
            }
            return output;
        },
        ellipse: function ({ x = 0, y = 0, width = 0.5, height = 1.0, res = 12, rotation = 0, start = 0, end = 1 }) {

            const output = [];
            const slice = 1 / res;

            const difference = Math.abs(end - start);
            for (let i = 0; i < res; i++) {
                const angle = slice * i;
                if (angle >= start && angle <= start + difference) {
                    output.push(G2.project.ellipse({ angle: angle, width, height }));
                }
            }

            G2.set.rotates(output, rotation);
            G2.set.offsets(output, x, y);
            return output;
        },
        mooning: function ({ x = 0, y = 0, height = 1.0, res = 24, rotation = 0, start = 0, end = 1, left = 0.75, right = 0.25 }) {
            const output = [];
            const slice = 1 / res;
            for (let i = 0; i < res; i++) {
                const angle = slice * i;
                if (angle >= start && angle <= end) {
                    output.push(G2.project.ellipse({ angle: angle, height: (angle < 0.5) ? left : right, width: height }));
                }
            }
            G2.set.rotates(output, rotation);
            G2.set.offsets(output, x, y);
            return output;
        }
    }
};